import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3CenterLeftIcon,
  BellIcon,
  CogIcon,
  HeartIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { FileKey, UserPlus } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NotificationDrawer from '../components/NotificationDrawer';
import { useApp } from '../context/usePortal';
import { useNotifications } from '../hooks/useNotifications';

const navigation = [
  { name: 'Payment', href: '/', icon: HomeIcon, current: true },
  {
    name: 'Referral Program',
    href: '/referral-program',
    icon: UserPlus,
    current: true,
    attentionIcon: '/assets/sparkles.gif',
    isHighlighted: true
  },
  {
    name: 'My Donations',
    href: '/donations',
    icon: HeartIcon,
    current: true,
    badge: 'NEW'
  }
];

const secondaryNavigation = [
  {
    name: 'Settings',
    href: '/settings',
    id: 'settings',
    icon: CogIcon,
    target: '_self'
  },
  {
    name: 'Help',
    href: 'https://t.me/paywithayeface',
    icon: QuestionMarkCircleIcon,
    target: '_blank'
  },
  {
    name: 'Terms & Conditions',
    href: 'https://aye-ai.org/user-tc/',
    icon: ShieldCheckIcon,
    target: '_blank'
  },
  {
    name: 'Privacy Policy',
    href: 'https://aye-ai.org/privacy-legal/',
    icon: FileKey,
    target: '_blank'
  }
];

const thirdNavigation = [
  { name: 'Logout', icon: ArrowLeftStartOnRectangleIcon }
];

function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function NavBadge({ label }: { label: string }) {
  return (
    <span className="ml-auto rounded-full bg-[#5CB891] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
      {label}
    </span>
  );
}

const logo = process.env.PUBLIC_URL + '/logo.png';

// Add custom styles for the sliding pulse animation
const pulseAnimation = `
  @keyframes orangePulse {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    20% {
      opacity: 0.8;
    }
    80% {
      opacity: 0.8;
    }
    100% {
      transform: translateX(200%);
      opacity: 0;
    }
  }
  .pulse-container {
    animation: orangePulse 1.5s ease-in-out infinite;
    animation-duration: 4s;
    pointer-events: none;
  }
`;

export default function Nav() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [referralPulseDisabled, setReferralPulseDisabled] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { logout } = useApp();

  const {
    notifications: allNotifications,
    unreadCount,
    markAsRead,
    deleteNotification,
    isDeleting
  } = useNotifications();

  // Show only first 5 notifications in popup/drawer
  const notifications = (allNotifications || []).slice(0, 5);

  // Close notifications when clicking outside (mobile only)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setNotificationsOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
      }
    }

    if (notificationsOpen) {
      // Only add click outside listener for mobile
      if (window.innerWidth < 1024) {
        document.addEventListener('mousedown', handleClickOutside);
        document.body.style.overflow = 'hidden';
      }
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [notificationsOpen]);

  const handleReferralClick = () => {
    // Disable pulse for this session
    setReferralPulseDisabled(true);
  };

  const handleLogout = () => {
    logout();
    window.location.replace('/login');
  };

  const isCurrent = (itemHref: string) => {
    return location.pathname === itemHref;
  };

  // Notification functions
  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  return (
    <>
      <style>{pulseAnimation}</style>
      {/* <div className="min-h-full"> */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-50 pb-4 pt-5">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex flex-shrink-0 items-center px-4">
                  <img className="h-8 w-auto" src={logo} alt="Aye logo" />
                </div>
                <nav
                  className="mt-5 h-full flex-shrink-0 divide-y divide-gray-100 overflow-y-auto"
                  aria-label="Sidebar"
                >
                  <div className="space-y-1 px-2">
                    {navigation.map((item) => (
                      <Link
                        to={item.href}
                        key={item.name}
                        // href={item.href}
                        id={item.name}
                        className={classNames(
                          isCurrent(item.href)
                            ? 'bg-gray-50 text-orange-600 hover:bg-white'
                            : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                          'group relative flex items-center overflow-hidden rounded-xl px-3 py-2 text-sm font-medium'
                        )}
                        aria-current={isCurrent(item.href) ? 'page' : undefined}
                        onClick={() => {
                          setSidebarOpen(false);
                          if (item.isHighlighted) {
                            handleReferralClick();
                          }
                        }}
                      >
                        <item.icon
                          className={classNames(
                            isCurrent(item.href)
                              ? 'text-orange-500'
                              : 'text-gray-400 group-hover:text-gray-500',
                            '-ml-1 mr-3 h-6 w-6 flex-shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                        {item.isHighlighted && !referralPulseDisabled && (
                          <div className="pulse-container absolute inset-0">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
                          </div>
                        )}
                        {item.attentionIcon && (
                          <img
                            src={item.attentionIcon}
                            alt="attention"
                            className="ml-4 h-7 w-7"
                          />
                        )}
                        {item.badge && <NavBadge label={item.badge} />}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-6 pt-6">
                    <div className="space-y-1 px-2">
                      {secondaryNavigation.map((item) => (
                        <Link
                          to={item.href}
                          key={item.name}
                          // href={item.href}
                          id={item.id}
                          className={classNames(
                            isCurrent(item.href)
                              ? 'bg-gray-50 text-orange-600 hover:bg-white'
                              : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                            'group flex items-center rounded-md px-3 py-2 text-sm font-medium'
                          )}
                          onClick={() => {
                            setSidebarOpen(false);
                          }}
                        >
                          <item.icon
                            className={classNames(
                              isCurrent(item.href)
                                ? 'text-orange-500'
                                : 'text-gray-400 group-hover:text-gray-500',
                              '-ml-1 mr-3 h-6 w-6 flex-shrink-0 stroke-[1.5]'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 pt-6">
                    <div className="space-y-1 px-2">
                      {thirdNavigation.map((item) => (
                        <a
                          key={item.name}
                          onClick={
                            item.name === 'Logout' ? handleLogout : () => {}
                          }
                          // to={item.name}
                          className={classNames(
                            // item.current
                            //   ? "bg-gray-50 text-orange-600 hover:bg-white"
                            //   :
                            'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                            'group flex items-center rounded-md px-3 py-2 text-sm font-medium'
                          )}
                        >
                          <item.icon
                            className={classNames(
                              //   item.current
                              //     ? "text-orange-500"
                              //     :
                              'text-gray-400 group-hover:text-gray-500',
                              '-ml-1 mr-3 h-6 w-6 flex-shrink-0'
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </nav>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true"></div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="bg-gray-10 flex flex-grow flex-col overflow-y-auto border-r-1 pb-4 pt-5">
          <div className="flex flex-shrink-0 items-center px-4">
            <img className="h-8 w-auto" src={logo} alt="Aye logo" />
          </div>
          <nav
            className="mt-5 flex flex-1 flex-col divide-y divide-gray-100 overflow-y-auto"
            aria-label="Sidebar"
          >
            <div className="space-y-1 px-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={classNames(
                    isCurrent(item.href)
                      ? 'bg-gray-50 text-orange-600 hover:bg-white'
                      : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                    'group relative flex items-center overflow-hidden rounded-xl px-3 py-2 text-sm font-medium'
                  )}
                  aria-current={isCurrent(item.href) ? 'page' : undefined}
                  onClick={() => {
                    if (item.isHighlighted) {
                      handleReferralClick();
                    }
                  }}
                >
                  <item.icon
                    className={classNames(
                      isCurrent(item.href)
                        ? 'text-orange-500'
                        : 'text-gray-400 group-hover:text-gray-500',
                      '-ml-1 mr-3 h-6 w-6 flex-shrink-0'
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate">{item.name}</span>
                  {item.isHighlighted && !referralPulseDisabled && (
                    <div className="pulse-container absolute inset-0">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
                    </div>
                  )}
                  {item.attentionIcon && (
                    <img
                      src={item.attentionIcon}
                      alt="attention"
                      className="ml-4 h-7 w-7"
                    />
                  )}
                  {item.badge && <NavBadge label={item.badge} />}
                </Link>
              ))}
            </div>
            <div className="mt-6 pt-6">
              <div className="space-y-1 px-2">
                {secondaryNavigation.map((item) => (
                  <Link
                    key={item.name}
                    id={item.id}
                    to={item.href}
                    target={item.target}
                    className={classNames(
                      isCurrent(item.href)
                        ? 'bg-gray-50 text-orange-600 hover:bg-white'
                        : 'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                      'group flex items-center rounded-md px-3 py-2 text-sm font-medium'
                    )}
                  >
                    <item.icon
                      className={classNames(
                        isCurrent(item.href)
                          ? 'text-orange-500'
                          : 'text-gray-400 group-hover:text-gray-500',
                        '-ml-1 mr-3 h-6 w-6 flex-shrink-0 stroke-[1.5]'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="mt-6 pt-6">
              <div className="space-y-1 px-2">
                {thirdNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to=""
                    onClick={item.name === 'Logout' ? handleLogout : () => {}}
                    className={classNames(
                      //   item.current
                      //     ? "bg-gray-50 text-orange-600 hover:bg-white"
                      //     :
                      'text-gray-900 hover:bg-gray-50 hover:text-gray-900',
                      'group flex items-center rounded-md px-3 py-2 text-sm font-medium'
                    )}
                  >
                    <item.icon
                      className={classNames(
                        // item.current
                        //   ? "text-orange-500"
                        //   :
                        'text-gray-400 group-hover:text-gray-500',
                        '-ml-1 mr-3 h-6 w-6 flex-shrink-0'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>

      <div className="flex flex-1 flex-col lg:pl-64">
        <div className="flex h-16 flex-shrink-0 border-b border-gray-200 bg-white lg:border-none">
          <button
            type="button"
            className="relative border-r border-gray-200 px-4 text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3CenterLeftIcon className="h-6 w-6" aria-hidden="true" />
            <img
              src="/assets/sparkles.gif"
              alt="attention"
              className="absolute right-0 top-7 h-7 w-7"
            />
          </button>
          <div className="flex flex-1 justify-between px-4 sm:px-6 lg:mx-auto lg:max-w-6xl lg:px-8">
            <div className="flex flex-1"></div>
            <div className="flex items-center justify-between md:ml-6">
              <div className="relative flex items-center">
                <button
                  type="button"
                  className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  onClick={toggleNotifications}
                >
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-xs text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Drawer */}
                <NotificationDrawer
                  isOpen={notificationsOpen}
                  onClose={() => setNotificationsOpen(false)}
                  notifications={notifications}
                  unreadCount={unreadCount}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  isDeleting={isDeleting}
                  notificationRef={notificationRef}
                />

                {/* <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 lg:rounded-md lg:p-2 lg:hover:bg-gray-50">
                      {/* <img
                        className="h-8 w-8 rounded-full"
                        src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                        alt=""
                      />
                      <span className="ml-3 hidden text-sm font-medium text-gray-700 lg:block">
                        <span className="sr-only">Open user menu for </span>
                        Profile
                      </span>
                      <ChevronDownIcon
                        className="ml-1 hidden h-5 w-5 flex-shrink-0 text-gray-400 lg:block"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "block px-4 py-2 text-sm text-gray-700"
                            )}
                          >
                            Settings
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              "block px-4 py-2 text-sm text-gray-700"
                            )}
                          >
                            Logout
                          </a>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
